import { sherbertFetch } from "./networkUtils/sherbertFetch.js";
import { SHERBERT_ASSETS_API_URL_V2 } from "../urls.js";
import { QueryBuilder } from "../utils/QueryBuilder.js";
/**
 * Presign an asset. The returned signature can be used to access the asset without authentication.
 * @param args - The arguments for presigning an asset
 * @param args.assetId - The ID of the asset to presign
 * @param args.abortSignal - [optional] The abort signal to use for the request
 * @param args.authorizationHeader - The authorization header to use for the request
 * @returns PresignResponse
 * @example
 * ```ts
 * const presignedAsset = await presignAsset({
 *   assetId: "123",
 *   authorizationHeader: () => `Bearer ${token}`,
 * });
 * ```
 */
export async function presignAsset({ assetId, abortSignal, authorizationHeader, }) {
    const response = await sherbertFetch(() => [
        `${SHERBERT_ASSETS_API_URL_V2}/${assetId}:presign`,
        {
            method: "POST",
            headers: {
                Authorization: authorizationHeader(),
                Accept: "application/json",
            },
            signal: abortSignal,
        },
    ]);
    const data = (await response.json());
    abortSignal?.throwIfAborted();
    return data;
}
/**
 * Get a presigned URL for an asset. The URL can be used to upload assets without authentication.
 * This is useful for uploading assets from a different device.
 * @param options - The options for getting a presigned URL.
 * @param options.abortSignal - [optional] The abort signal to use for the request.
 * @param options.authorizationHeader - The authorization header to use for the request.
 * @param options.expires - [optional] The expiration time of the presigned URL, ISO 8601 duration string. Defaults to 10 minutes.
 * @param options.tenant - [optional] The tenant of the asset.
 * @param options.folderName - [optional] The name of the folder the asset is in.
 * @param options.folderId - [optional] The ID of the folder the asset is in.
 * @returns PresignResponse
 * @example
 * ```ts
 * const presignedUrl = await getPresignedUrl({
 *   authorizationHeader: () => `Bearer ${token}`,
 *   expires: "PT600S", // ISO 8601 duration string
 * });
 * ```
 */
export async function getPresignedUrl({ abortSignal, authorizationHeader, expires = "PT600S", tenant, folderName, folderId, }) {
    const query = QueryBuilder()
        .add("tenant", tenant)
        .add("folderName", folderName)
        .add("folderId", folderId)
        .toString();
    const response = await sherbertFetch(() => [
        `${SHERBERT_ASSETS_API_URL_V2}:presign${query ? `?${query}` : ""}`,
        {
            method: "POST",
            headers: {
                Authorization: authorizationHeader(),
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            signal: abortSignal,
            body: JSON.stringify({ expires }),
        },
    ]);
    const data = (await response.json());
    abortSignal?.throwIfAborted();
    return data;
}
